/**
 * MAIN STREAMING COMPONENT
 * This is the primary streaming component used for /LiveVideoStreaming/:playlistId routes
 * It includes the Song Request feature and other streaming functionality
 */
import React, { useContext, useState, useEffect, useRef } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import snapshotCapture from "../../services/snapshotCapture";
import GavelIcon from "@mui/icons-material/Gavel";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { SocketContext } from "../../context/socket";
import {
  getUserId,
  getErrorString,
  fullName,
  isOwnerId,
} from "../../constants/function";

import {
  updateStreamCount,
  setStreamCount,
} from "../../action/streamCount";

import { useAgoraRTC } from "../../services/useAgoraRTC";
import PushPinIcon from "@mui/icons-material/PushPin";
import AgoraRTC from "agora-rtc-sdk-ng";
import Loader from "../../components/Loader";
import TipPopup from "../../components/TipPopup";
import NftList from "../../components/DirectChat/nftList";
import StreamTippers from "../../components/Stream/AudiencCardContainer/StreamTippers";
import SuccessPopup from "../../components/SuccessPopup";
import StreamingControls from "../../components/Stream/StreamingControls";
import TopListPopup from "../../components/TopListPopup";
import ActiveListings from "../../components/Stream/ActiveListings";
import StreamingChat from "../../components/Stream/StreamingChat";
import notifier from "../../components/Notifier";
import emitter from "../../EventEmitter";
import StreamHeader from "../../components/Stream/AudiencCardContainer/StreamHeader";
import ApiService from "../../services/api-service";
import cookieStorage from "../../services/cookie-storage";
import StreamingService from "./StreamingService";
import { Images } from "../../assets/common";
import { start } from "../../action/streamSong";  // the action that sets { pos, etc. }


function Streaming({
  streamData: propStreamData,
  playlistId: propPlaylistId,
  title,
  goal,
  goalDescription,
  isJoined: propIsJoined,
  setIsJoined: propSetIsJoined,
  tokenData: propTokenData,
  setTokenData: propSetTokenData,
  obs = false,
  match, // Router prop
  history, // Router prop
  location, // Router prop
}) {
  
  // --------------------------------------------------
  // 1) Basic user & role
  // --------------------------------------------------
  const userId = getUserId();
  
  // Get playlistId from URL if not provided as prop
  const playlistId = propPlaylistId || match?.params?.playlistId;
  
  // State for stream data - will be fetched if not provided as prop
  const [streamData, setStreamData] = useState(propStreamData || null);
  const [isJoined, setIsJoined] = useState(propIsJoined || false);
  const [loading, setLoading] = useState(false);
  
  // Debug loading state changes
  const setLoadingWithDebug = (value) => {
    console.log(`[Streaming] Setting loading to ${value}`, new Error().stack);
    setLoading(value);
  };
  
  // Check ownership using multiple possible properties (same as auto-rejoin logic)
  // Wait for streamData to be available before determining ownership
  const isOwner = streamData && userId ? (
    (streamData.owner && streamData.owner.toString() === userId.toString()) ||
    (streamData.createdBy && streamData.createdBy.toString() === userId.toString()) ||
    (streamData.createdByDetails?._id && streamData.createdByDetails._id.toString() === userId.toString())
  ) : false;
  
  // Check URL parameters for role (from auto-rejoin)
  const urlParams = new URLSearchParams(window.location.search);
  const urlRole = urlParams.get('role');
  const urlEnableVideo = urlParams.get('enableVideo') === 'true';
  
  const initialRole = urlRole || (isOwner ? "host" : "audience");
  
  // Add error boundary state
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Local token data state as fallback
  const [localTokenData, setLocalTokenData] = useState(null);
  const effectiveTokenData = propTokenData || localTokenData;
  const effectiveSetTokenData = propSetTokenData || setLocalTokenData;

  const dispatch = useDispatch();
  
  // Track if we've already restored tokens to prevent loops
  const [tokenRestored, setTokenRestored] = useState(false);
  
  // Track component mount state
  const isMountedRef = useRef(true);
  
  // Refs to store current values for cleanup
  const socketRef = useRef(null);
  const streamDataRef = useRef(null);
  const playlistIdRef = useRef(null);
  const userIdRef = useRef(null);
  const isJoinedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    
    // Ensure container is visible on mount
    const container = document.getElementById("streaming-main-container");
    if (container) {
      container.style.display = "flex";
    }
    
    return () => {
      isMountedRef.current = false;
      // Clean up global flags when component unmounts
      window.hasEmittedRejoinHost = false;
      window.hasReconnectedAsHost = false;
      
      // Notify backend that user is leaving the stream (use refs to get current values)
      const currentSocket = socketRef.current;
      const currentStreamData = streamDataRef.current;
      const currentPlaylistId = playlistIdRef.current;
      const currentUserId = userIdRef.current;
      const currentIsJoined = isJoinedRef.current;
      
      if (currentSocket && currentSocket.connected && currentIsJoined) {
        console.log("[Streaming] Component unmounting - notifying backend user is leaving");
        currentSocket.emit("stream-end", {
          JWT: cookieStorage.getCookie("Authorization"),
          channelName: currentStreamData?._id || currentPlaylistId,
          role: "audience"
        });
      }
    };
  }, []);
  
  // Fetch stream data if not provided as prop
  useEffect(() => {
    const fetchStreamData = async () => {
      if (streamData || !playlistId) return; // Already have data or no playlistId
      
      try {
        setLoading(true);
        const response = await ApiService.PLAYLIST_DETAIL({
          _id: playlistId,
          pageNo: 1,
          pageSize: 1,
        });
        
        if (response?.result) {
          console.log('[Streaming] Stream data loaded:', response.result);
          console.log('[Streaming] Stream data properties - owner:', response.result.owner, 'createdBy:', response.result.createdBy, 'createdByDetails:', response.result.createdByDetails);
          setStreamData(response.result);
          
          // Check if stream was already active (for page refresh scenarios)
          if (response.result.token && response.result.channelName && !tokenRestored && isMountedRef.current) {
            console.log('[Streaming] Stream already active, restoring connection');
            effectiveSetTokenData({
              token: response.result.token,
              appId: response.result.appId,
              channelName: response.result.channelName,
              uid: response.result.uid
            });
            setTokenRestored(true);
          }
        }
      } catch (error) {
        console.error('[Streaming] Error fetching stream data:', error);
        notifier.error('Failed to load stream data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchStreamData();
  }, [playlistId, streamData]);

  const [isCohost, setIsCohost] = useState(false);

  // --------------------------------------------------
  // 2) UI states + Socket
  // --------------------------------------------------
  const { Socket: socket } = useContext(SocketContext);
  const [message, setMessage] = useState(null);
  const [success, setSuccess] = useState(false);

  const [liveUser, setLiveUser] = useState([]);
  const [tippers, setTippers] = useState(false);
  const [showNft, setShowNft] = useState(false);
  const [openTip, setOpenTip] = useState(false);
  const [userData, setUserData] = useState({});
  const [openUser, setOpenUser] = useState(false);
  
  // Host interaction handlers
  const handleTipHost = () => {
    if (streamData?.createdByDetails) {
      emitter.emit("openTipModal", { user: streamData.createdByDetails });
    } else {
      notifier.error("Unable to tip host - host information not available");
    }
  };

  const handleFollowHost = async () => {
    if (!streamData?.createdByDetails?._id) {
      notifier.error("Unable to follow host - host information not available");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        following: streamData.createdByDetails._id,
        status: true, // Always follow (could be enhanced to check current status)
      };
      
      await ApiService.FOLLOW(payload);
      notifier.success("Successfully followed the host!");
    } catch (error) {
      notifier.error("Failed to follow host");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribeHost = async () => {
    if (!streamData?.createdByDetails?._id) {
      notifier.error("Unable to subscribe to host - host information not available");
      return;
    }

    try {
      setLoading(true);
      const response = await ApiService.SUBSCRIBE_ARTIST({
        artistId: streamData.createdByDetails._id,
        currency: "siki",
      });
      
      notifier.success("Successfully subscribed to the host!");
    } catch (error) {
      notifier.error("Failed to subscribe to host - insufficient balance?");
    } finally {
      setLoading(false);
    }
  };

  
  const localVideoTrackRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  // Fullscreen
  const [isFullScreen, setIsFullScreen] = useState(false);
  // Active Listings
  const [showActiveListings, setShowActiveListings] = useState(false);

  // Audio/Video toggles
  const [isMute, setIsMute] = useState(false);
  const [video, setVideo] = useState(true);

  // Decide if audio/video are enabled initially
  const [enableAudio] = useState(true);
  const [enableVideo] = useState(urlEnableVideo || isOwner);
  
  console.log('[Streaming] Role determination:', {
    userId: userId?.toString(),
    streamOwner: streamData?.owner?.toString(),
    streamCreatedBy: streamData?.createdBy?.toString(),
    streamCreatedByDetailsId: streamData?.createdByDetails?._id?.toString(),
    isOwner,
    urlRole,
    urlEnableVideo,
    finalRole: initialRole,
    enableVideo,
    enableAudio
  });

  const [lastSecond, setLastSecond] = useState(0);
  const { songs, vol } = useSelector(state => state.streamSong);

  const videoContainerRef = useRef(null);


  
  // --------------------------------------------------
  // 3) Agora Hook with error handling
  // --------------------------------------------------
  let agoraHookResult;
  try {
    agoraHookResult = useAgoraRTC({
      appId: effectiveTokenData?.appId,
      channel: effectiveTokenData?.channelName,
      token: effectiveTokenData?.token,
      uid: effectiveTokenData?.uid ?? null,
      role: initialRole,
      enableAudio,
      enableVideo,
      onJoinSuccess: (assignedUid) => {
        console.log('[useAgoraRTC] Join success callback, clearing loading state');
        if (isMountedRef.current) {
          setLoading(false);
          setHasError(false);
          setErrorMessage('');
        }
      },
    });
  } catch (error) {
    console.error('[Streaming] Error initializing Agora hook:', error);
    setHasError(true);
    setErrorMessage('Failed to initialize streaming connection');
    // Provide fallback values
    agoraHookResult = {
      localAudioTrack: null,
      localVideoTrack: null,
      localScreenTrack: null,
      isScreenSharing: false,
      setLocalVideoTrack: () => {},
      musicTrack: null,
      publishMusicTrack: () => {},
      stopMusicTrack: () => {},
      remoteUsers: [],
      joinChannel: () => {},
      leaveChannel: () => {},
      muteAudio: () => {},
      unmuteAudio: () => {},
      enableLocalVideo: () => {},
      disableLocalVideo: () => {},
      setClientRole: () => {},
      startScreenShare: () => {},
      stopScreenShare: () => {},
      client: null,
    };
  }
  
  const {
    localAudioTrack,
    localVideoTrack,
    localScreenTrack,
    isScreenSharing,
    setLocalVideoTrack,
    musicTrack,
    publishMusicTrack,
    stopMusicTrack,
    remoteUsers,
    joinChannel,
    leaveChannel,
    muteAudio,
    unmuteAudio,
    enableLocalVideo,
    disableLocalVideo,
    setClientRole,
    startScreenShare,
    stopScreenShare,
    client,
  } = agoraHookResult;


  // --------------------------------------------------
  // 4) Aggregator logic from Redux
  // --------------------------------------------------
  const { index, track, pos } = useSelector((state) => state.streamSong);
  const { streamCount } = useSelector((state) => state.streamCount);
  const seek = useSelector((state) => state.seek);

  const [callCount, setCallCount] = useState(0);

  
  
  // --------------------------------------------------
  // 5) Socket events: watchers, aggregator, stream-ended
  // --------------------------------------------------
  useEffect(() => {
    if (!client) {
      return;
    }
  
    const handleUserLeft = (user) => {
      setLiveUser((prev) => prev.filter((u) => u?.user?.uid !== user.uid));
    };
  
    // Register Agora event listeners
    client.on("user-left", handleUserLeft);
  
    return () => {
      client.off("user-left", handleUserLeft);
    };
  }, [client]);

  useEffect(() => {
    if (!socket) {
      console.log("[Streaming] Socket not available");
      return;
    }

    console.log("[Streaming] Setting up socket listeners - socket connected:", socket.connected);
  
    const handleAudienceInStream = (data) => {
      const effectivePlaylistId = streamData?._id || playlistId;
      console.log("[Streaming] handleAudienceInStream:", {
        channelName: data.channelName,
        streamDataId: streamData?._id,
        playlistId: playlistId,
        effectivePlaylistId: effectivePlaylistId,
        liveUsersCount: data.liveUsers?.length,
        match: data.channelName === effectivePlaylistId
      });
    
      if (data.channelName === effectivePlaylistId) {
        if (data.liveUsers && data.liveUsers.length > 0) {
          console.log("[Streaming] => Received audience-in-stream with:", data.liveUsers.length, "users");
          console.log("[Streaming] => Users details:", data.liveUsers.map(u => ({
            id: u.user?._id,
            name: u.user?.firstName || 'Unknown',
            role: u.role,
            uid: u.uid
          })));
          
          // Always use the server data as it's the source of truth
          setLiveUser([...data.liveUsers]);
          console.log("[Streaming] => Updated liveUser state to count:", data.liveUsers.length);

          if (!isOwner && typeof data.streamCount === "number") {
            dispatch(setStreamCount(data.streamCount));
            console.log("[Streaming] => Synced streamCount from audience-in-stream payload:", data.streamCount);
          }

          // Ensure any newly joined audience sees the up-to-date stream count immediately.
          if (isOwner && socket && typeof streamCount === "number") {
            const channelName = streamData?._id || playlistId;
            socket.emit("livestream-streamcount", {
              streamCount,
              channelName,
            });
            console.log("[Streaming] => Broadcasted current streamCount for new audience:", {
              streamCount,
              channelName,
            });
          }
        } else {
          console.warn("[Streaming] => No liveUsers found in audience-in-stream event");
          // Don't automatically clear - this might be a temporary state
          console.log("[Streaming] => Keeping current liveUser state, count:", liveUser.length);
        }
      } else {
        console.warn("[Streaming] => Ignoring audience-in-stream event for different channel:", data.channelName, "expected:", effectivePlaylistId);
      }
    };
  
    socket.on("audience-in-stream", handleAudienceInStream);
    
    // aggregator from host => if I'm not host
    socket.on("livestream-streamcount", (data) => {
      const effectivePlaylistId = streamData?._id || playlistId;
      if (data.channelName === effectivePlaylistId && !isOwner) {
        console.log("[Streaming] => Received livestream-streamcount event:", data);

        dispatch(setStreamCount(data.streamCount));
        console.log("[Streaming] => Updated streamCount in Redux:", data.streamCount);

      }
    });
  
    const handleStreamEnded = (data) => {
      const effectivePlaylistId = streamData?._id || playlistId;
      if (data.channelName === effectivePlaylistId) {
        // Only redirect if we're not the owner who initiated the end
        if (!isOwner || (data.endedBy && data.endedBy !== userId)) {
          console.log("[Streaming] => Stream ended by host. Redirecting...");
          window.location.replace(`${window.location.origin}/app/Home`);
        } else {
          console.log("[Streaming] => Stream ended by current user, handling locally");
        }
      }
    };
  
    socket.on("stream-ended", handleStreamEnded);
    
    // Handle socket disconnection events
    socket.on("disconnect", () => {
      console.log("[Streaming] Socket disconnected");
    });
    
    socket.on("connect", () => {
      console.log("[Streaming] Socket reconnected");
    });
  
    return () => {
      socket.off("audience-in-stream", handleAudienceInStream);
      socket.off("livestream-streamcount");
      socket.off("stream-ended", handleStreamEnded);
      socket.off("disconnect");
      socket.off("connect");
    };
  }, [socket, isOwner, streamData, playlistId, dispatch, streamCount]);

  // Initialize user in liveUser list when they join the stream
  useEffect(() => {
    if (isJoined && liveUser.length === 0) {
      console.log("[Streaming] Initializing user in liveUser list, isOwner:", isOwner, "userId:", userId);
      const currentUser = {
        user: {
          _id: userId,
          uid: userId,
          firstName: isOwner ? "Host" : "Viewer",
          lastName: "",
          role: isOwner ? "host" : "audience"
        },
        role: isOwner ? "host" : "audience",
        uid: userId
      };
      console.log("[Streaming] Setting initial liveUser:", [currentUser]);
      setLiveUser([currentUser]);
    }
  }, [isOwner, isJoined, userId, liveUser.length]);

  // Debug effect to track liveUser changes
  useEffect(() => {
    console.log("[Streaming] liveUser state changed:", {
      count: liveUser.length,
      users: liveUser.map(u => ({
        id: u.user?._id,
        name: u.user?.firstName,
        role: u.role,
        uid: u.uid
      }))
    });
  }, [liveUser]);

  // Keep refs updated with current values for cleanup
  useEffect(() => {
    socketRef.current = socket;
    streamDataRef.current = streamData;
    playlistIdRef.current = playlistId;
    userIdRef.current = userId;
    isJoinedRef.current = isJoined;
  }, [socket, streamData, playlistId, userId, isJoined]);

  // Handle browser close/refresh - notify backend user is leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (socketRef.current && socketRef.current.connected && isJoinedRef.current) {
        console.log("[Streaming] Browser closing - notifying backend user is leaving");
        // Use synchronous fetch since socket.emit might not complete in time
        try {
          navigator.sendBeacon('/livestream/end/audience', JSON.stringify({
            JWT: cookieStorage.getCookie("Authorization"),
            channelName: streamDataRef.current?._id || playlistIdRef.current
          }));
        } catch (error) {
          // Fallback to socket emit
          socketRef.current.emit("stream-end", {
            JWT: cookieStorage.getCookie("Authorization"),
            channelName: streamDataRef.current?._id || playlistIdRef.current,
            role: "audience"
          });
        }
      }
    };

    const handleUnload = () => {
      if (socketRef.current && socketRef.current.connected && isJoinedRef.current) {
        console.log("[Streaming] Page unloading - notifying backend user is leaving");
        socketRef.current.emit("stream-end", {
          JWT: cookieStorage.getCookie("Authorization"),
          channelName: streamDataRef.current?._id || playlistIdRef.current,
          role: "audience"
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, []);

  // Get history from useHistory hook for reliable access
  const historyFromHook = useHistory();
  
  // Track if stream-end was already sent to prevent duplicates
  const leaveStreamSentRef = useRef(false);
  
  // Listen for route changes using history
  useEffect(() => {
    if (!historyFromHook) {
      console.log("[Streaming] History not available for route listening");
      return;
    }

    const unlisten = historyFromHook.listen((location, action) => {
      // If user navigates away from streaming page, notify backend
      if (!location.pathname.includes('/LiveVideoStreaming') && isJoinedRef.current && !leaveStreamSentRef.current) {
        console.log("[Streaming] Route change detected - leaving streaming page, notifying backend and cleaning up media");
        leaveStreamSentRef.current = true; // Mark as sent to prevent duplicates
        
        // Clean up audio/video tracks when navigating away
        if (localAudioTrackRef.current) {
          try {
            if (client) {
              client.unpublish(localAudioTrackRef.current).catch(err => 
                console.warn("[Streaming] Failed to unpublish audio on route change:", err)
              );
            }
            localAudioTrackRef.current.close();
            localAudioTrackRef.current = null;
            console.log("[Streaming] Audio track cleaned up on navigation");
          } catch (error) {
            console.error("[Streaming] Error cleaning up audio track:", error);
          }
        }
        
        if (localVideoTrackRef.current) {
          try {
            if (client) {
              client.unpublish(localVideoTrackRef.current).catch(err => 
                console.warn("[Streaming] Failed to unpublish video on route change:", err)
              );
            }
            localVideoTrackRef.current.close();
            localVideoTrackRef.current = null;
            console.log("[Streaming] Video track cleaned up on navigation");
          } catch (error) {
            console.error("[Streaming] Error cleaning up video track:", error);
          }
        }
        
        // Call leaveChannel to properly disconnect from Agora
        if (leaveChannel) {
          try {
            leaveChannel();
            console.log("[Streaming] Left Agora channel on navigation");
          } catch (error) {
            console.error("[Streaming] Error leaving channel:", error);
          }
        }
        
        if (socketRef.current && socketRef.current.connected) {
          socketRef.current.emit("stream-end", {
            JWT: cookieStorage.getCookie("Authorization"),
            channelName: streamDataRef.current?._id || playlistIdRef.current,
            role: "audience"
          });
        }
      }
    });

    return unlisten;
  }, [historyFromHook]);

  useEffect(() => {
    if (!client) {
      return;
    }

  

    const updateLiveUser = (newUsers) => {
      console.log("[Streaming] => Updating liveUser with newUsers:", newUsers);

      setLiveUser((prev) => {
        const updatedList = [...prev, ...newUsers].filter(
          (user, index, self) =>
            index === self.findIndex((u) => u?.user?.uid === user?.user?.uid)
        );
        console.log("[Streaming] => Updated liveUser list:", updatedList);

        return updatedList;
      });
    };
  
    
  
   }, [client]);




useEffect(() => {
  if (!client) {
    return;
  }

  let userCount = 0;

  const handleUserJoined = (user) => {
    userCount += 1;
    console.log("[Agora] => User joined => UID:", user.uid);
    console.log("[Agora] => Total users in channel:", userCount);
  };

  const handleUserLeft = (user) => {
    userCount -= 1;

  };


  // Register Agora event listeners
  client.on("user-joined", handleUserJoined);
  client.on("user-left", handleUserLeft);


  return () => {
    client.off("user-joined", handleUserJoined);
    client.off("user-left", handleUserLeft);
  };
}, [client]);



  // Log watchers when they change
  useEffect(() => {
    if (liveUser?.length) {
    } else {
    }
  }, [liveUser]);

  // 5B) Listen for cohost assignment
  useEffect(() => {
    if (!socket || !client) {
      return;
    }

    const handleCohostAssigned = async (data) => {
      if (data.userId === userId) {
        try {
          await setClientRole("host");
          await enableLocalVideo();
          setIsCohost(true);
          setPinnedUid("local");
        } catch (error) {
        }
      }
    };

    socket.on("cohost-assigned", handleCohostAssigned);
    socket.on("makeCoHost", handleCohostAssigned);

    return () => {
      socket.off("cohost-assigned", handleCohostAssigned);
      socket.off("makeCoHost", handleCohostAssigned);
    };
  }, [socket, userId, client, setClientRole, enableLocalVideo]);

  // --------------------------------------------------
  // 6) aggregator partial increments
// ===== Host Aggregator Code (to be added in Streaming.jsx) =====
// --- In Streaming.jsx ---
// (Place this among your other useEffect hooks in the Streaming component)

useEffect(() => {
  if (!track) return;

  setLastSecond(0);

  const intervalId = setInterval(() => {
    try {
      if (track.currentState === "playing") {
        const currentTime = track.getCurrentTime();
        if (isNaN(currentTime)) throw new Error("Invalid currentTime");
        dispatch(start({ index, songs, pos: currentTime, track, vol }));
      }
    } catch (error) {
    }
  }, 1000);

  return () => {
    clearInterval(intervalId);
  };
}, [track, dispatch, index, songs, vol]);

  // The aggregator partial increments effect
  useEffect(() => {
    // your aggregator code that references pos
  }, [pos]);

const AGGREGATOR_SKIP_THRESHOLD = 2; // seconds threshold for a "seek"

useEffect(() => {
  // Only run if a track exists, the current user is the host,
  // and we are not currently in a seek operation.
  if (!track || !isOwner || (seek && seek.started)) return;

  const isPlaying = track.currentState === "playing";
  if (!isPlaying) return; // Only count while playing



  // Get track duration (in whole seconds) and current playback second.
  const duration = Math.floor(track.duration) || 1;
  const currentSecond = Math.floor(pos);

  if (lastSecond === undefined || lastSecond === null) {
    setLastSecond(0);
    return;
  }

  if (currentSecond - lastSecond > AGGREGATOR_SKIP_THRESHOLD) {
    setLastSecond(currentSecond);
    return;
  }

  // For each new second that hasn’t been processed, add a partial increment.
  if (currentSecond > lastSecond) {
    for (let sec = lastSecond + 1; sec <= currentSecond; sec++) {
      const watchersCount = liveUser.length || 1;
      const fraction = 1 / Math.max(duration, 1);     
     const increment = watchersCount * fraction;
      dispatch(updateStreamCount(increment));
      console.log("[Aggregator] Added", increment.toFixed(3), "for second", sec);
    }
    setLastSecond(currentSecond);
  }
}, [pos, track, isOwner, liveUser, lastSecond, dispatch, seek]);

// When a new track starts, reset only the aggregator’s local timer.
// (This allows the global streamCount to accumulate continuously.)
useEffect(() => {
  if (track) {
    console.log("[Aggregator] Resetting lastSecond for new track:", track._id);

    setLastSecond(0);
  }
}, [track?._id]);






// Emit the host’s total stream count so that audience clients display the host’s count.

let lastEmitTime = 0;

useEffect(() => {
  if (socket && isOwner && streamCount !== undefined) {
    const now = Date.now();
    if (now - lastEmitTime < 1000) {
      return;
    }
    lastEmitTime = now;

    socket.emit("livestream-streamcount", {
      streamCount,
      channelName: streamData?._id,
    });
  }
}, [streamCount, socket, isOwner, streamData]);


let lastUpdateTime = 0;

useEffect(() => {
  if (!socket) return;

  const handleStreamCount = (data) => {
    const now = Date.now();
    if (now - lastUpdateTime < 1000) {
      return;
    }
    lastUpdateTime = now;

    dispatch(setStreamCount(data.streamCount));
  };

  socket.on("livestream-streamcount", handleStreamCount);

  return () => {
    socket.off("livestream-streamcount", handleStreamCount);
  };
}, [socket, dispatch]);

//---
// switch video audio

useEffect(() => {
  if (localVideoTrack) {
    localVideoTrackRef.current = localVideoTrack;
    
    // Clear loading state once video track is available
    console.log('[Streaming] Local video track available, clearing loading state');
    if (isMountedRef.current) {
      setLoading(false);
    }
    
    // Ensure owner has their video pinned
    if (isOwner && pinnedUid !== "local") {
      console.log('[Streaming] Force setting pinnedUid to local for owner with video track');
      setPinnedUid("local");
    }
    
    // Start snapshot capture if we're the host
    if (isOwner && streamData?._id) {
      // Wait for video to be rendered and playing
      const startSnapshotCapture = () => {
        // Try to find the video element that Agora creates
        const videoElements = document.querySelectorAll('video');
        let targetVideo = null;
        
        // Find the video element that's actually playing
        for (const video of videoElements) {
          if (video.srcObject && !video.paused && video.readyState >= 2) {
            targetVideo = video;
            break;
          }
        }
        
        if (targetVideo) {
          snapshotCapture.startCapturing(targetVideo, streamData._id);
        } else {
          setTimeout(startSnapshotCapture, 1000);
        }
      };
      
      setTimeout(startSnapshotCapture, 2000);
    }
  }
  
  return () => {
    // Stop snapshot capture when component unmounts or video track changes
    if (isOwner) {
      snapshotCapture.stopCapturing();
    }
  };
}, [localVideoTrack, isOwner, streamData?._id]);

useEffect(() => {
  if (localAudioTrack) {
    localAudioTrackRef.current = localAudioTrack;
  }
}, [localAudioTrack]);

const fetchAvailableDevices = async () => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioDevices = devices.filter((device) => device.kind === "audioinput");
    const videoDevices = devices.filter((device) => device.kind === "videoinput");


    return { audioDevices, videoDevices };
  } catch (error) {
    return { audioDevices: [], videoDevices: [] };
  }
};

const switchAudio = async (micLabel) => {

  try {
    const { audioDevices } = await fetchAvailableDevices();
    const selectedDevice = audioDevices.find((device) => device.label === micLabel);

    if (!selectedDevice) {
      return;
    }

    // Unpublish and close the current audio track
    if (localAudioTrackRef.current) {
      await client.unpublish(localAudioTrackRef.current);
      localAudioTrackRef.current.close();
    }

    // Create a new audio track with the selected device
    const newAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
      microphoneId: selectedDevice.deviceId,
    });

    // Publish the new audio track
    await client.publish(newAudioTrack);

    // Update the localAudioTrack reference
    localAudioTrackRef.current = newAudioTrack;

  } catch (error) {
  }
};

useEffect(() => {
}, []);



async function switchVideo(cameraLabel) {
  try {
    const { videoDevices } = await fetchAvailableDevices();
    const selectedDevice = videoDevices.find((device) => device.label === cameraLabel);

    if (!selectedDevice) {
      return;
    }

    if (!client) {
      return;
    }

    if (localVideoTrackRef.current) {
      return;
    }

    // Create a new video track with the selected device
    const newVideoTrack = await AgoraRTC.createCameraVideoTrack({
      cameraId: selectedDevice.deviceId,
    });

    // Publish the new video track
    await client.publish(newVideoTrack);

    // Update the local references
    localVideoTrackRef.current = newVideoTrack;
    setLocalVideoTrack(newVideoTrack); // Update the state to trigger re-render

  } catch (error) {
  }
}


  // --------------------------------------------------
  // 7) Pinned cohost logic
  // --------------------------------------------------
  const [pinnedUid, setPinnedUid] = useState(null);
  const pinnedIsLocal = pinnedUid === "local";

  // Update pinnedUid when ownership status changes
  useEffect(() => {
    console.log('[Streaming] Updating pinnedUid - isOwner:', isOwner, 'currentPinnedUid:', pinnedUid);
    if (isOwner && pinnedUid !== "local") {
      console.log('[Streaming] Setting pinnedUid to local for owner');
      setPinnedUid("local"); // Owner pins their own video
    } else if (!isOwner && !pinnedUid) {
      if (streamData?.owner) {
        setPinnedUid(streamData.owner); // Audience pins the owner's video
      } else if (remoteUsers.length) {
        setPinnedUid(remoteUsers[0].uid); // Fallback to the first remote user
      }
    }
  }, [isOwner, streamData?.owner, remoteUsers]);

  // --------------------------------------------------
  // 8) End stream emitter
  // --------------------------------------------------
  useEffect(() => {
    const handleEndStream = () => {
      if (isOwner) endStream();
      else setSuccess(true);
    };
    emitter.on("endStream", handleEndStream);

    return () => {
      emitter.removeListener("endStream", handleEndStream);
    };
  }, [isOwner]);

async function endStream() {
  // Stop snapshot capture when ending stream
  if (isOwner) {
    console.log('[Streaming] Stopping snapshot capture');
    snapshotCapture.stopCapturing();
  }
  
  if (socket && streamData?._id) {
    socket.emit("stream-ended", { 
      channelName: streamData._id,
      endedBy: userId 
    });
    console.log("[Streaming] => Emitting stream-ended:", {
      channelName: streamData._id,
      endedBy: userId
    });
  }

  try {
    // End the stream for everyone (host action)
    const response = await StreamingService.endLiveStream(streamData._id);

    if (response.success) {
      console.log("[Streaming] => Successfully ended livestream");
    } else {
      console.error("[Streaming] => Failed to end livestream:", response.message);
    }
  } catch (error) {
    console.error("[Streaming] => Error ending livestream:", error);
  }

  // Clean up local tracks and leave channel
  await localCleanupAndExit();
}
  // --------------------------------------------------
  // 9) Start or Join stream
  // --------------------------------------------------
 const startStreamAPI = async () => {
  if (!streamData?._id) {
    return;
  }
  
  // Check if stream was already started (has token from GoLive flow)
  if (streamData.token && streamData.channelName) {
    console.log("Stream already started via GoLive, skipping startStreamAPI");
    // Set tokenData from the existing streamData
    if (isMountedRef.current) {
      effectiveSetTokenData({ 
        token: streamData.token, 
        appId: streamData.appId, 
        channelName: streamData.channelName, 
        uid: streamData.uid 
      });
    }
    setLoading(false);
    return;
  }
  
  try {
    setLoading(true);
    const res = await StreamingService.startLiveStream({
      channelName: streamData._id,
      goalAmount: goal,
      goalTitle: title,
      goalDescription: goalDescription,
    });
    if (!res?.success) {
      // If the stream is already live, try to join as host instead
      if (res?.message?.includes("already live")) {
        console.log("[Streaming] Stream already live, attempting to join as host");
        try {
          const rejoinRes = await StreamingService.joinLiveStream({
            channelName: streamData._id,
            platform: "web",
          });
          if (rejoinRes?.success) {
            const { token, appId, channelName, uid } = rejoinRes.data || {};
            console.log("[Streaming] Join as host successful:", { token, appId, channelName, uid });
            if (isMountedRef.current) {
              effectiveSetTokenData({ token, appId, channelName, uid });
              setLoading(false);
            }
            return;
          }
        } catch (rejoinErr) {
          console.error("[Streaming] Join as host failed:", rejoinErr);
        }
      }
      if (res?.message) {
        notifier.error(res.message);
      } else {
        notifier.error("Failed to start livestream");
      }
      setLoading(false);
      return;
    }
    const { token, appId, channelName, uid } = res.data || {};
    console.log("[Streaming] startStreamAPI response:", { token, appId, channelName, uid });
    if (!token) {
      console.error("[Streaming] No token in response!");
      setLoading(false);
      return;
    }
    console.log("[Streaming] Setting tokenData with:", { token, appId, channelName, uid });
    if (isMountedRef.current) {
      effectiveSetTokenData({ token, appId, channelName, uid });
    }

    // --- Emit stream-start socket event as host ---
    if (socket) {
      const jwt = cookieStorage.getCookie("Authorization");
      socket.emit("stream-start", {
        JWT: jwt,
        channelName: streamData._id,
        role: "host",
        goalAmount: goal,
        goalTitle: title,
        goalDescription: goalDescription,
        platform: "web",
        isNew: true,
        fromObs: false,
      });
    }
    // ---------------------------------------------
  } catch (err) {
    console.error("[Streaming] => startStreamAPI => error =>", err);
    
    // Check if it's an "already live" error and try to join as host
    if (err?.response?.data?.message?.includes("already live")) {
      console.log("[Streaming] Stream already live (from catch), attempting to join as host");
      try {
        const rejoinRes = await StreamingService.joinLiveStream({
          channelName: streamData._id,
          platform: "web",
        });
        if (rejoinRes?.success) {
          const { token, appId, channelName, uid } = rejoinRes.data || {};
          console.log("[Streaming] Join as host successful:", { token, appId, channelName, uid });
          if (isMountedRef.current) {
            effectiveSetTokenData({ token, appId, channelName, uid });
            setLoading(false);
          }
          return;
        }
      } catch (rejoinErr) {
        console.error("[Streaming] Join as host failed:", rejoinErr);
      }
    }
    
    setLoading(false);
  }
};

  const joinStreamAPI = async () => {
    if (!streamData?._id) {
      return;
    }
    try {
      setLoading(true);
      const res = await StreamingService.joinLiveStream({
        channelName: streamData._id,
        platform: "web",
      });
      if (!res?.success) {
        notifier.error(res?.message || "Failed to join livestream");
        setLoading(false);
        return;
      }
      const { token, appId, channelName, uid } = res.data || {};
      if (!token) {
        setLoading(false);
        return;
      }
      if (isMountedRef.current) {
        effectiveSetTokenData({ token, appId, channelName, uid });
      }
    } catch (err) {
      console.error("[Streaming] => joinStreamAPI => error =>", err);
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // 10) Cleanup
  // --------------------------------------------------
  function cleanupAudio() {
    // stop your local track if needed
  }

 async function localCleanupAndExit(redirect = true) {
  if (socket && streamData?._id) {
    socket.emit("leave-livestream", { channelName: streamData._id, userId });
    console.log("[Streaming] => Emitting leave-livestream:", {
      channelName: streamData._id,
      userId,
    });
  }

  try {
    const response = await StreamingService.exitLiveStream({
      channelName: streamData._id,
      userId: userId,
    });

    if (response.success) {
      console.log("[Streaming] => Successfully exited livestream");
    } else {
      console.error("[Streaming] => Failed to exit livestream:", response.message);
    }
  } catch (error) {
    console.error("[Streaming] => Error exiting livestream:", error);
  }

  // Properly unpublish and close local tracks to prevent duplicate video on reconnect
  try {
    if (localVideoTrackRef.current && client) {
      await client.unpublish(localVideoTrackRef.current);
      localVideoTrackRef.current.close();
      localVideoTrackRef.current = null;
    }
    if (localAudioTrackRef.current && client) {
      await client.unpublish(localAudioTrackRef.current);
      localAudioTrackRef.current.close();
      localAudioTrackRef.current = null;
    }
  } catch (cleanupError) {
    console.error("[Streaming] => Error cleaning up local tracks:", cleanupError);
  }

  cleanupAudio();
  leaveChannel();

  if (redirect) {
    window.location.replace(`${window.location.origin}/app/Home`);
  }
}

  // --------------------------------------------------
  // 12) Join once token is available
  // --------------------------------------------------
  const [alreadyJoined, setAlreadyJoined] = useState(false);
  const [apiCallInProgress, setApiCallInProgress] = useState(false);
  
  useEffect(() => {
    // Don't proceed if we don't have basic stream data
    if (!streamData || !userId) {
      console.log("[Streaming] Waiting for streamData and userId...", { streamData: !!streamData, userId: !!userId });
      return;
    }
    
    // Prevent multiple API calls
    if (apiCallInProgress) {
      console.log("[Streaming] API call in progress, skipping");
      return;
    }
    
    console.log("[Streaming] Token effect - effectiveTokenData:", effectiveTokenData, "alreadyJoined:", alreadyJoined);
    if (!effectiveTokenData?.token && !alreadyJoined) {
      // Use URL role to determine if user should be host, fallback to isOwner check
      const shouldBeHost = urlRole === 'host' || isOwner;
      console.log("[Streaming] No token, calling", shouldBeHost ? "startStreamAPI" : "joinStreamAPI", 
                  "urlRole:", urlRole, "isOwner:", isOwner, "shouldBeHost:", shouldBeHost);
      setApiCallInProgress(true);
      if (shouldBeHost) {
        startStreamAPI().finally(() => setApiCallInProgress(false));
      } else {
        joinStreamAPI().finally(() => setApiCallInProgress(false));
      }
    } else if (effectiveTokenData?.token && !alreadyJoined) {
      console.log("[Streaming] Have token, calling joinChannel");
      joinChannel();
      setAlreadyJoined(true);
      setIsJoined(true);
      if (propSetIsJoined) {
        propSetIsJoined(true);
      }
      
      // Clear loading immediately since we have the token and are joining
      if (isMountedRef.current) {
        setLoading(false);
      }
      
      // Fallback timeout to clear loading state if onJoinSuccess doesn't fire
      setTimeout(() => {
        if (isMountedRef.current) {
          console.log('[Streaming] Fallback timeout clearing loading state');
          setLoading(false);
        }
      }, 5000); // 5 second timeout
    } else {
      console.log("[Streaming] Already joined or no conditions met, skipping");
    }
  }, [effectiveTokenData?.token, alreadyJoined, urlRole, isOwner, streamData, userId, apiCallInProgress]);


  // After we’ve successfully joined, tell the socket we’re in
  useEffect(() => {
    if (alreadyJoined && socket && streamData?._id) {
      console.log(
        "[Streaming] => Emitting join-livestream => channel:",
        streamData._id,
        " userId:",
        userId
      );
      socket.emit("join-livestream", {
        channelName: streamData._id,
        JWT: cookieStorage.getCookie("Authorization"), // Send JWT token for authentication
        role: initialRole, // Include role information
        platform: 'web', // Identify this as a web user
        userId: userId, // Include user ID
        enableVideo: true, // Web users typically have video enabled
      });
      
      // Clear loading state since we've successfully joined
      if (isMountedRef.current) {
        console.log('[Streaming] Successfully joined, clearing loading state');
        setLoading(false);
      }
      
      // If we're the owner and rejoining after a page refresh, emit rejoin-host
      // Only do this if we have a token from storage (indicates page refresh)
      if (isOwner && tokenRestored && !window.hasEmittedRejoinHost) {
        console.log("[Streaming] => Emitting rejoin-host after page refresh");
        window.hasEmittedRejoinHost = true;
        socket.emit("rejoin-host", {
          channelName: streamData._id,
          userId,
          agoraUid: effectiveTokenData?.uid,
          role: "host"
        });
      }
    }
  }, [alreadyJoined, socket, streamData?._id, userId, initialRole, isOwner, urlRole, effectiveTokenData?.uid]);

  // --------------------------------------------------
  // 13) Browser unload => leave-livestream or reconnect
  // --------------------------------------------------

  useEffect(() => {
  if (!socket || !isOwner || !streamData?._id || !effectiveTokenData?.uid) return;

  const handleReconnect = () => {
    console.log("[Streaming] => Socket reconnected, checking if rejoin needed...");
    // Only rejoin if we haven't already done so
    if (!window.hasReconnectedAsHost) {
      window.hasReconnectedAsHost = true;
      socket.emit("rejoin-host", {
        channelName: streamData._id,
        userId,
        agoraUid: effectiveTokenData.uid,
        role: "host"
      });
      // Reset the flag after a delay to allow future reconnects
      setTimeout(() => {
        window.hasReconnectedAsHost = false;
      }, 5000);
    }
  };

  socket.on("reconnect", handleReconnect);

  return () => {
    socket.off("reconnect", handleReconnect);
  };
}, [socket, isOwner, streamData?._id, userId, effectiveTokenData?.uid]);

  // Track if component is actually unmounting vs just re-rendering
  const [isUnmounting, setIsUnmounting] = useState(false);
  
  // Debug header visibility
  useEffect(() => {
    if (streamData && isJoined) {
      setTimeout(() => {
        const headerContainer = document.getElementById('streaming-header-bar');
        const streamingContainer = document.getElementById('streaming-main-container');
        console.log('[Streaming] Header visibility check:', {
          headerContainer: !!headerContainer,
          headerVisible: headerContainer?.style.display !== 'none',
          headerOffsetHeight: headerContainer?.offsetHeight,
          streamingContainer: !!streamingContainer,
          streamingContainerDisplay: streamingContainer?.style.display
        });
      }, 1000);
    }
  }, [streamData, isJoined]);
  
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      console.log("[Streaming] => handleBeforeUnload called");
      // Emit leave events synchronously on page unload
      if (socket && streamData?._id) {
        // For host, emit stream-ended
        if (isOwner) {
          socket.emit("stream-ended", { 
            channelName: streamData._id,
            endedBy: userId 
          });
        }
        // For all users, emit leave-livestream
        socket.emit("leave-livestream", {
          channelName: streamData._id,
          userId,
        });
      }
    };
    
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handleBeforeUnload);
    
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handleBeforeUnload);
      // Don't emit any leave events here - only on actual page unload
    };
  }, [socket, streamData?._id, userId, isOwner]);
  // --------------------------------------------------
  // 14) Mute & Video
  // --------------------------------------------------
  const handleMuteToggle = async () => {
    if (isMute) {
      await unmuteAudio();
      setIsMute(false);
    } else {
      await muteAudio();
      setIsMute(true);
    }
  };

  const handleVideoToggle = async () => {
    if (video) {
      await disableLocalVideo();
      setVideo(false);
    } else {
      await enableLocalVideo();
      setVideo(true);
    }
  };

  // --------------------------------------------------
  // 15) Fullscreen
  // --------------------------------------------------
  const openFullScreen = async () => {
    const elem = document.getElementById("__streaming_container");
    if (elem && !isFullScreen) {
      setIsFullScreen(true);
      await elem.requestFullscreen();
    }
  };

  const closeFullScreen = async () => {
    if (document.exitFullscreen) {
      setIsFullScreen(false);
      await document.exitFullscreen();
    }
  };

  // --------------------------------------------------
  // 16) Tip
  // --------------------------------------------------
  const [tipLoading, setTipLoading] = useState(false);
  const handleTip = async (amount) => {
    console.log("[Streaming] => handleTip => amount =>", amount);
    if (!amount) {
      setOpenTip(false);
      return;
    }
    try {
      setTipLoading(true);
      setOpenTip(false);
      // do tip logic here
      console.log("[Streaming] => handleTip => tip logic done");
      setTipLoading(false);
    } catch (err) {
      console.error("[Streaming] => handleTip => error =>", err);
      setTipLoading(false);
      notifier.error(getErrorString(err));
    }
  };

  // --------------------------------------------------
  // 17a) Pinned area
  // --------------------------------------------------

  const renderPinnedVideoArea = () => {
    console.log('[renderPinnedVideoArea] Called - pinnedIsLocal:', pinnedIsLocal, 'pinnedUid:', pinnedUid, 'localVideoTrack:', !!localVideoTrack, 'isOwner:', isOwner);
    
    // Force render video area for owner (even if track not ready yet)
    if (isOwner) {
      console.log('[renderPinnedVideoArea] Rendering owner video area');
      return (
        <Box
          className="pinned_area"
          sx={{
            position: "relative",
            flex: 1,
            backgroundColor: "var(--background-color, #000)",
            minHeight: "100%",
            minWidth: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {localVideoTrack ? (
            <div
              id="local-video-container"
              style={{ 
                width: "100%", 
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--background-color, #000)"
              }}
              ref={(ref) => {
                if (ref && localVideoTrack) {
                  console.log("[renderPinnedVideoArea] Playing local video track on ref:", ref);
                  try {
                    localVideoTrack.play(ref);
                    // Ensure video maintains aspect ratio
                    const videoElement = ref.querySelector('video');
                    if (videoElement) {
                      videoElement.style.width = '100%';
                      videoElement.style.height = '100%';
                      videoElement.style.objectFit = 'contain';
                    }
                  } catch (error) {
                    console.error("[renderPinnedVideoArea] Error playing video track:", error);
                  }
                }
              }}
            />
          ) : (
            <Box sx={{ textAlign: 'center', color: '#fff' }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                📹 Initializing Camera...
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Your video will appear here shortly
              </Typography>
              {!video && (
                <Typography variant="body2" sx={{ mt: 2, color: '#ff9800' }}>
                  Video is currently disabled. Click the camera button to enable.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      );
    }
    
    if (pinnedIsLocal) {
      return (
        <Box
          className="pinned_area"
          sx={{
            position: "relative",
            flex: 1,
            backgroundColor: "var(--background-color, #000)",
            minHeight: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {localVideoTrack ? (
            <div
              id="local-video-container"
              style={{ 
                width: "100%", 
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--background-color, #000)"
              }}
              ref={(ref) => {
                if (ref && localVideoTrack) {
                  console.log("[renderPinnedVideoArea] Playing local video track on ref:", ref);
                  try {
                    localVideoTrack.play(ref);
                    // Ensure video maintains aspect ratio
                    const videoElement = ref.querySelector('video');
                    if (videoElement) {
                      videoElement.style.width = '100%';
                      videoElement.style.height = '100%';
                      videoElement.style.objectFit = 'contain';
                    }
                  } catch (error) {
                    console.error("[renderPinnedVideoArea] Error playing video track:", error);
                  }
                }
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "var(--background-color-rgba, rgba(0,0,0,0.8))",
                color: "#fff"
              }}
            >
              <Typography variant="h5" sx={{ mb: 2 }}>
                {isOwner ? "📹 Starting Camera..." : "Waiting for host video..."}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Local video track: {localVideoTrack ? 'Available' : 'Not available'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Video enabled: {video ? 'Yes' : 'No'}
              </Typography>
              {isOwner && !localVideoTrack && (
                <Typography variant="body2" sx={{ mt: 2, color: "#ff9800" }}>
                  Please allow camera access if prompted
                </Typography>
              )}
            </Box>
          )}
        </Box>
      );
    }
    // Handle remote pinned video
    const pinnedRemoteObj = remoteUsers.find((r) => r.uid === pinnedUid);
    if (pinnedRemoteObj && pinnedRemoteObj.videoTrack) {
      return (
        <Box
          className="pinned_area"
          sx={{
            position: "relative",
            flex: 1,
            backgroundColor: "var(--background-color, #000)",
            minHeight: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            id="remote-video-container"
            style={{ 
              width: "100%", 
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "var(--background-color, #000)"
            }}
            ref={(ref) => {
              if (ref && pinnedRemoteObj.videoTrack) {
                console.log("[renderPinnedVideoArea] Playing remote video track on ref:", ref);
                try {
                  pinnedRemoteObj.videoTrack.play(ref);
                  // Ensure video maintains aspect ratio
                  const videoElement = ref.querySelector('video');
                  if (videoElement) {
                    videoElement.style.width = '100%';
                    videoElement.style.height = '100%';
                    videoElement.style.objectFit = 'contain';
                  }
                } catch (error) {
                  console.error("[renderPinnedVideoArea] Error playing remote video track:", error);
                }
              }
            }}
          />
        </Box>
      );
    }
    return (
      <Box
        className="pinned_area"
        sx={{
          position: "relative",
          flex: 1,
          backgroundColor: "var(--background-color, #000)",
          minHeight: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ color: "#fff", textAlign: "center" }}>No pinned track</Typography>
      </Box>
    );
  };

  // --------------------------------------------------
  // 17b) Cohost container
  // --------------------------------------------------
  const renderCohostContainer = () => {
    // local user (if cohost) + remote cohosts except pinned
    const cohosts = [];

    if ((isOwner || isCohost) && pinnedUid !== "local") {
      cohosts.push({ uid: "local", local: true });
    }

    remoteUsers.forEach((r) => {
      if (r.uid !== pinnedUid) {
        cohosts.push({ uid: r.uid, local: false });
      }
    });

    if (cohosts.length === 0) return null;

    return (
      <Box
        className="cohost_container"
        sx={{
          width: 300,
          height: "100%",
          backgroundColor: "transparent",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px 20px 20px 10px",
          overflowY: "auto",
          flexShrink: 0,
        }}
      >
        {cohosts.map((c) => {
          let displayName = "";
          let trackObj = null;
          if (c.local) {
            displayName = "You (cohost)";
            trackObj = localVideoTrack;
          } else {
            const found = remoteUsers.find((r) => r.uid === c.uid);
            if (found) {
              displayName = fullName(found.user, 12) || `UID ${found.uid}`;
              trackObj = found.videoTrack;
            }
          }
          return (
            <Box
              key={c.uid}
              sx={{
                position: "relative",
                backgroundColor: "#2a2a2a",
                width: "100%",
                height: 400,
                cursor: "pointer",
                borderRadius: "12px",
                overflow: "hidden",
                border: "2px solid rgba(255, 255, 255, 0.1)",
                flexShrink: 0,
              }}
            >
              {trackObj ? (
                <div
                  style={{ width: "100%", height: "100%" }}
                  ref={(ref) => ref && trackObj.play(ref)}
                />
              ) : (
                <Typography sx={{ color: "#fff", padding: "5px" }}>
                  {displayName}
                </Typography>
              )}
              
              {pinnedUid !== (c.local ? "local" : c.uid) && (
              <PushPinIcon
                style={{
                  position: "absolute",
                  top: 5,
                  right: 5,
                  width: 24,
                  height: 24,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setPinnedUid(c.local ? "local" : c.uid);
                }}
              />
            )}
            </Box>
          );
        })}
      </Box>
    );
  };

  // --------------------------------------------------
  // 17c) Audience icons => vertical column
  // --------------------------------------------------
  const renderAudienceIcons = () => {
    // Audience list removed - now shown in header dropdown
    return null;
  };

  // --------------------------------------------------
  // 18) Render
  // --------------------------------------------------
  
  // Show error if there's a critical error
  if (hasError) {
    return (
      <Box sx={{ 
        width: "100vw", 
        height: "100vh", 
        position: "fixed",
        top: 0,
        left: 0,
        backgroundColor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column"
      }}>
        <Typography variant="h5" sx={{ color: "#fff", mb: 2 }}>
          Streaming Error
        </Typography>
        <Typography variant="body1" sx={{ color: "#fff", mb: 3 }}>
          {errorMessage}
        </Typography>
        <IconButton 
          onClick={() => window.location.reload()}
          sx={{ 
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            color: "#fff",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" }
          }}
        >
          Reload Page
        </IconButton>
      </Box>
    );
  }

  // Debug state values before checking
  console.log('[Streaming] Render check - streamData:', !!streamData, streamData?._id, 'userId:', userId, 'playlistId:', playlistId);
  
  // Use streamData._id as playlistId if available
  const effectivePlaylistId = streamData?._id || playlistId;
  
  // Show loading if we don't have basic data yet - but allow rendering if we have token (means we're connected)
  if ((!streamData || !effectivePlaylistId) && !effectiveTokenData?.token) {
    console.log('[Streaming] Showing loading screen - streamData:', !!streamData, 'userId:', !!userId, 'playlistId:', !!playlistId, 'effectivePlaylistId:', !!effectivePlaylistId, 'hasToken:', !!effectiveTokenData?.token);
    return (
      <Box sx={{ 
        width: "100vw", 
        height: "100vh", 
        position: "fixed",
        top: 0,
        left: 0,
        backgroundColor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <Loader wait message="Loading stream data..." />
      </Box>
    );
  }
  
  console.log('[Streaming] Main render - about to render full UI');
  return (
    <Box
      id="__streaming_container"
      className="__streaming_container"
      sx={{ 
        width: "100vw", 
        height: "100vh",
        position: "fixed",
        top: 0,
        left: 0,
        backgroundColor: "#000",
        overflow: "hidden",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column"
      }}
    >
      {/* Header Bar - Absolutely positioned */}
      <Box 
        id="streaming-header-bar"
        sx={{ 
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "60px",
          backgroundColor: "rgba(0, 0, 0, 0.95)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          zIndex: 10000,
          boxSizing: "border-box",
          boxShadow: "0 2px 10px rgba(0,0,0,0.5)"
        }}
      >
        {console.log('[Streaming] StreamHeader render - streamData:', !!streamData, 'isJoined:', isJoined, 'liveUser count:', liveUser?.length || 0, 'liveUser details:', liveUser?.map(u => u.user?.firstName || 'Unknown'), 'isOwner:', isOwner)}
        {streamData ? (
          <StreamHeader 
            title={streamData.title || streamData.name || 'Live Stream'}
            watchers={liveUser?.length || 0}
            isOwner={isOwner}
            isJoined={isJoined}
            playlistId={effectivePlaylistId}
            streamData={streamData}
            liveUser={liveUser}
            totalUsers={liveUser?.length || 0}
            closeFullscreen={closeFullScreen}
            leaveStreaming={() => setSuccess(true)}
            isFullScreen={isFullScreen}
            openFullScreen={openFullScreen}
            goal={goal}
            title={title}
            goalDescription={goalDescription}
            leaveStreamSentRef={leaveStreamSentRef}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ color: '#fff' }}>Loading header...</Typography>
          </Box>
        )}
      </Box>

      {/* Loading - only show for tip loading */}
      {tipLoading && (
        <Loader
          wait
          _class="__loader_inside"
          message={message || "Processing..."}
        />
      )}

      {/* Goal / Tippers - Moved inside video area */}


      {/* Main area: pinned + cohosts + audience */}
      <Box sx={{ 
        position: "absolute",
        top: "60px", // Start below the header
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
        backgroundColor: "var(--background-color, #000)",
        display: "flex",
        flexDirection: "column"
      }}>
        {/* Video Content Area */}
        <Box sx={{ 
          flex: 1,
          display: "flex",
          position: "relative"
        }}>
          {console.log('[Streaming] About to call renderPinnedVideoArea')}
          {renderPinnedVideoArea()}
          {console.log('[Streaming] About to call renderCohostContainer')}
          {renderCohostContainer()}
          {console.log('[Streaming] About to call renderAudienceIcons')}
          {renderAudienceIcons()}
          
          {/* Goal/Tippers Overlay - Higher above controls */}
          {(goal > 0 || title) && (
            <Box sx={{ 
              position: "absolute",
              bottom: 200, // Much higher above the controls bar
              right: 20,
              zIndex: 25
            }}>
              <StreamTippers
                title={title || ""}
                goal={goal || 0}
                goalProgress={0}
                liveUser={liveUser.length}
                isOwner={isOwner}
                setStreamTippers={setTippers}
                channelName={streamData._id}
                isJoined={isJoined}
                goalDescription={goalDescription || ""}
              />
            </Box>
          )}
          
          {/* Auction Icon - Middle Right - HIDDEN */}
          {/* <Box
            sx={{
              position: "absolute",
              top: "50%",
              right: "20px",
              transform: "translateY(-50%)",
              zIndex: 15
            }}
          >
            <IconButton
              onClick={() => setShowActiveListings(true)}
              sx={{
                backgroundColor: "rgba(53, 53, 53, 0.9)",
                color: "#fff",
                borderRadius: "50%",
                boxShadow: "0px 4px 8px rgba(0,0,0,0.3)",
                "&:hover": {
                  backgroundColor: "rgba(106, 106, 159, 0.9)"
                },
              }}
            >
              <GavelIcon />
            </IconButton>
          </Box> */}
        </Box>
        
        {/* Controls Bar - Fixed at bottom */}
        {effectiveTokenData?.token && (
          <Box
            sx={{
              height: "80px",
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingX: 3,
              flexShrink: 0,
              position: "relative"
            }}
          >
            {/* Chat Overlay - Bottom Left, input overlapping controls */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0, // At the very bottom so input overlaps controls
                left: 20,
                width: 350,
                height: "50vh",
                maxHeight: 500,
                zIndex: 30 // Higher than controls
              }}
            >
              <StreamingChat
                playlistId={effectivePlaylistId}
                isOwner={isOwner}
                closeFullscreen={closeFullScreen}
                leaveStreaming={() => setSuccess(true)}
                streamData={streamData}
                artistId={streamData?.owner}
                isSubscribed
              />
            </Box>
            
            <StreamingControls
              role={isOwner ? "host" : isCohost ? "cohost" : "audience"}
              showRecordButton={false} 
              isOwner={isOwner}
              isJoined={isJoined}
              isVideo={video}
              isMute={isMute}
              video={video}
              mute={handleMuteToggle}
              disableVideo={handleVideoToggle}
              leaveStreaming={() => setSuccess(true)}
              isScreenSharing={isScreenSharing}
              shareScreen={startScreenShare}
              stopShare={stopScreenShare}
              isFullScreen={isFullScreen}
              openFullScreen={openFullScreen}
              closeFullScreen={closeFullScreen}
              musicTrack={musicTrack}
              switchAudio={switchAudio}
              switchVideo={switchVideo} 
              publishMusicTrack={publishMusicTrack}
              stopMusicTrack={stopMusicTrack}
              playlistId={playlistId || streamData?._id}
              hostId={streamData?.createdBy || streamData?.createdByDetails?._id || streamData?.owner || "61736afa0a4eff0de720b4aa"}
              onTipHost={handleTipHost}
              onFollowHost={handleFollowHost}
              onSubscribeHost={handleSubscribeHost}
            />
          </Box>
        )}
      </Box>

      {/* Auction icon moved to video area */}
      {showActiveListings && (
        <ActiveListings
          streamData={streamData?.createdBy}
          userId={userId}
          role={isOwner || isCohost ? "host" : "audience"}
          isOwner={isOwner}
          onClose={() => setShowActiveListings(false)}
        />
      )}

      {/* Audience popup */}
      {openUser && (
        <TopListPopup
          followings={[]}
          channelName={streamData._id}
          isOwner={isOwner}
          isUsers
          title={`Live Audience (${liveUser.length})`}
          open={openUser}
          data={liveUser}
          handleClose={() => setOpenUser(false)}
          onTipPoupOpen={(selectedUser) => {
            setUserData(selectedUser);
            setOpenTip(true);
          }}
          onNftPoupOpen={(selectedUser) => {
            setUserData(selectedUser);
            setShowNft(true);
          }}
          onRequestToHost={(id, idx, user) => {
            console.log("[TopListPopup] => requestToHost =>", { id, idx, user });
            socket.emit("requestToCoHost", {
              userId: id,
              role: "host",
              channelName: streamData._id,
              host: userId,
              joined: false,
            });
            setOpenUser(false);
          }}
        />
      )}

      {/* NFT popup */}
      {showNft && (
        <NftList
          style={{ right: 0, width: 444, zIndex: 100000 }}
          onClose={() => setShowNft(false)}
          fullName={fullName(userData?.user)}
          toUser={userData._id}
        />
      )}

      {/* Tip popup */}
      {openTip && (
        <TipPopup
          artist={fullName(userData?.user)}
          id={userData._id}
          open={openTip}
          isArtist
          handleClose={handleTip}
        />
      )}

      {/* End stream => success popup */}
      {success && (
        <SuccessPopup
          open={success}
          message="Stream ended"
          handleClose={() => {
            console.log("[SuccessPopup] => user pressed close => isOwner?", isOwner);
            if (!isOwner) {
              localCleanupAndExit();
            } else {
              endStream();
            }
          }}
        />
      )}

    </Box>
  );
}

export default Streaming;
